// Demo Mode prose rewrites: the request queue and cache behind DemoText.
//
// Prose the engine cannot spoof in place (notes, evidence, briefs) stays
// redacted in Demo Mode. DemoText asks for a display-only rewrite of it from
// POST /api/demo-mode/rewrite/ and shows that instead once the browser has
// checked it. Nothing here writes to Vuex business state, storage, analytics
// or the clipboard: results live only in this module's in-memory cache and in
// the mounted DemoText that asked.
//
//   - Requests go out only while Demo Mode is ready and the signed-in user is
//     allowed to use it (a superuser). Blocks wait ~50ms to batch, at most 4
//     blocks per request and 4 requests in flight, inside every server limit.
//     Oversized blocks or fake maps are never sent and stay redacted.
//   - No automatic retries: a redact, an HTTP error, a timeout or a malformed
//     reply is cached as redact for that block and context.
//   - The cache key is tenant + user + record + text + fakes map + Demo
//     generation. Leaving Demo Mode (exit, revoke, logout), a new generation,
//     a tenant or user change cancels queued and in-flight requests, drops
//     late replies and clears the cache.

import axios from 'axios';
import { effectScope } from 'vue';
import store from '@/store';
import { DEMO_STATUS } from '@/store/modules/demoMode';
import { demoModeAllowedForCurrentUser } from '@/utils/demoController';
import {
  CLINICAL_UNIT_RE,
  LONG_DIGIT_RUN_RE,
  demoFakesFor,
  demoNameMapChangedAt,
  demoRewriteIdentityHits,
} from '@/utils/demoSpoof';

export const REWRITE_URL = '/api/demo-mode/rewrite/';
export const REWRITE_LIMITS = Object.freeze({
  blocksPerRequest: 4,
  inFlight: 4,
  blockChars: 4000,
  requestChars: 30000,
  fakePairs: 500,
  fakeChars: 256,
  bodyBytes: 1024 * 1024,
  batchDelayMs: 50,
  timeoutMs: 30000,
  cacheEntries: 500,
  // Transient failures (429, timeout, network): at most this many sends per
  // block and context, the extra one only on a remount or reactivation.
  attemptsPerBlock: 2,
  // Cooldown after a 429: Retry-After, clamped to this range; the default
  // when the header is absent or unreadable.
  retryAfterMinMs: 1000,
  retryAfterMaxMs: 120000,
  retryAfterDefaultMs: 30000,
  viewportMargin: '200px 0px',
  nameMapSettleMs: 150,
  nameMapSettleMaxMs: 1000,
});

const codePoints = (text) => Array.from(text).length;

// --- Context ---

function currentContext() {
  const user = store.getters['auth/current_user'];
  return {
    tenant: store.getters['tenant/current_tenant_slug'] ?? null,
    user: user?.id ?? null,
    generation: store.getters['demoMode/generation'],
  };
}

function contextId(ctx = currentContext()) {
  return JSON.stringify([ctx.tenant, ctx.user, ctx.generation]);
}

function sendingAllowed() {
  return store.getters['demoMode/status'] === DEMO_STATUS.READY && demoModeAllowedForCurrentUser() === true;
}

// Small stable hash (cyrb53) of the fakes map, so the key names the mapping
// without repeating it.
function hash(str) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i += 1) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function mappingRevision(fakes) {
  return hash(JSON.stringify(Object.keys(fakes).sort().map((k) => [k, fakes[k]])));
}

function fakesFit(fakes) {
  const entries = Object.entries(fakes);
  if (entries.length > REWRITE_LIMITS.fakePairs) return false;
  return entries.every(
    ([k, v]) => k && v && codePoints(k) <= REWRITE_LIMITS.fakeChars && codePoints(String(v)) <= REWRITE_LIMITS.fakeChars
  );
}

// What a block would be sent as right now: its key, fakes map and whether it
// may be sent at all. null for empty text (nothing is ever sent for it).
export function describeRewrite({ text, record = null }) {
  const source = text == null ? '' : String(text);
  if (!source.trim()) return null;
  const fakes = demoFakesFor([source]);
  const recordId = record == null || record === '' ? null : String(record);
  const ctx = currentContext();
  const key = JSON.stringify([ctx.tenant, ctx.user, ctx.generation, recordId, source, mappingRevision(fakes)]);
  const sendable = codePoints(source) <= REWRITE_LIMITS.blockChars && fakesFit(fakes);
  return { key, text: source, record: recordId, fakes, sendable };
}

// --- Browser checks on a returned rewrite ---

// A run of 7+ digits from the original that survives in the output, unless
// that exact run is followed by the same lab unit in both. Leading zeros are
// kept (digits are compared as strings). Returns the number of survivors.
export function longNumberHits(original, rewrite) {
  const runs = [];
  for (const m of String(original).matchAll(LONG_DIGIT_RUN_RE)) {
    const unit = String(original).slice(m.index + m[0].length).match(CLINICAL_UNIT_RE);
    runs.push({ digits: m[0], unit: unit ? unit[1].toLowerCase() : null });
  }
  if (!runs.length) return 0;
  let hits = 0;
  for (const m of String(rewrite).matchAll(LONG_DIGIT_RUN_RE)) {
    const out = m[0];
    const after = String(rewrite).slice(m.index + out.length).match(CLINICAL_UNIT_RE);
    const outUnit = after ? after[1].toLowerCase() : null;
    const exempt = outUnit && runs.some((r) => r.digits === out && r.unit === outUnit);
    if (exempt) continue;
    let survived = false;
    for (let i = 0; i + 7 <= out.length && !survived; i += 1) {
      const window = out.slice(i, i + 7);
      survived = runs.some((r) => r.digits.includes(window));
    }
    if (survived) hits += 1;
  }
  return hits;
}

// Every check a rewrite must pass before it is shown or reused. Run again on
// cache hits and whenever more names are learned. Counts only.
export function checkRewrite(original, rewrite, fakes) {
  const hits = longNumberHits(original, rewrite) + demoRewriteIdentityHits(original, rewrite, fakes);
  return { ok: hits === 0, hits };
}

const stats = { requests: 0, rejected: 0, failed: 0, superseded: 0, aborted: 0 };

export function demoRewriteStats() {
  return { ...stats };
}

export function reportRejectedRewrite(hits) {
  stats.rejected += hits > 0 ? 1 : 0;
}

const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const timing = { startedAt: null, firstReadableAt: null, lastReadableAt: null, base: { ...stats } };
let routeMarkedAt = -Infinity;

function startTiming() {
  timing.startedAt = clock();
  timing.firstReadableAt = null;
  timing.lastReadableAt = null;
  timing.base = { ...stats };
}

function stopTiming() {
  timing.startedAt = null;
  timing.firstReadableAt = null;
  timing.lastReadableAt = null;
}

export function markDemoRewriteRoute() {
  if (store.getters['demoMode/status'] === DEMO_STATUS.OFF) return;
  startTiming();
  routeMarkedAt = clock();
  for (const req of [...inflight]) abortIfUnneeded(req);
}

export function noteDemoTextReadable() {
  if (timing.startedAt === null) return;
  const now = clock();
  if (timing.firstReadableAt === null) timing.firstReadableAt = now;
  timing.lastReadableAt = now;
}

// --- Queue and cache ---
//
// Outcomes are terminal or transient. Terminal ones are cached for the
// context: an ok rewrite, and a redact for backend (redact or missing
// result), malformed, oversized or http (400, 403 or any other HTTP error).
// Transient ones (rate-limit, timeout, network) are not cached as final: the
// block stays blurred, and a remount or KeepAlive reactivation of the
// component that shows it may send it once more, after any 429 cooldown.
// The attempt count lives here, keyed by block and context, so navigating
// back and forth cannot buy more attempts. An ordinary re-render never asks.
export const TRANSIENT_REASONS = Object.freeze(['rate-limit', 'timeout', 'network']);
const TRANSIENT = new Set(TRANSIENT_REASONS);

// key -> { status: 'ok', text } | { status: 'redact', reason }
const cache = new Map();
const transientFailures = new Map(); // key -> { reason, retryAt }
const attempts = new Map(); // key -> sends so far in this context
let pausedUntil = 0; // after a 429, nothing is sent before this time
const pending = new Map(); // key -> item (not sent yet)
const inflight = new Set(); // { controller, items }
let batchTimer = null;
let contextAtWork = null;
let watchScope = null;

const watched = new Map();
const onScreen = new Set();
let viewportObserver = null;
let viewportEpoch = 0;

function onViewport(entries) {
  for (const entry of entries) {
    if (!watched.has(entry.target)) continue;
    if (entry.isIntersecting) onScreen.add(entry.target);
    else onScreen.delete(entry.target);
  }
}

function watchElement(el) {
  const count = watched.get(el) || 0;
  watched.set(el, count + 1);
  if (count) return;
  if (!viewportObserver) {
    viewportObserver = new IntersectionObserver(onViewport, { rootMargin: REWRITE_LIMITS.viewportMargin });
  }
  viewportObserver.observe(el);
}

function unwatchElement(el) {
  const count = watched.get(el);
  if (!count) return;
  if (count > 1) {
    watched.set(el, count - 1);
    return;
  }
  watched.delete(el);
  onScreen.delete(el);
  viewportObserver?.unobserve(el);
}

function watchSubscriber(item, sub, el) {
  if (el?.nodeType !== 1 || typeof IntersectionObserver === 'undefined') return;
  item.watchers.set(sub, { el, epoch: viewportEpoch });
  watchElement(el);
}

function unwatchSubscriber(item, sub) {
  const w = item.watchers.get(sub);
  if (!w) return;
  item.watchers.delete(sub);
  if (w.epoch === viewportEpoch) unwatchElement(w.el);
}

function unwatchItem(item) {
  for (const sub of [...item.watchers.keys()]) unwatchSubscriber(item, sub);
}

function stopViewport() {
  viewportObserver?.disconnect();
  viewportObserver = null;
  watched.clear();
  onScreen.clear();
  viewportEpoch += 1;
}

function isOnScreen(item) {
  for (const w of item.watchers.values()) if (onScreen.has(w.el)) return true;
  return false;
}

function cacheGet(key) {
  if (!cache.has(key)) return undefined;
  const value = cache.get(key);
  cache.delete(key);
  cache.set(key, value);
  return value;
}

function cacheSet(key, value) {
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > REWRITE_LIMITS.cacheEntries) cache.delete(cache.keys().next().value);
}

function deliver(subscribers, key, result, fakes) {
  for (const cb of [...subscribers]) {
    try {
      cb({ key, ...result, fakes });
    } catch {
      /* one component must not stop the others */
    }
  }
}

// Cancels everything and forgets everything: queued and in-flight requests,
// the batch timer and the cache. Late replies are dropped by their context.
export function resetDemoRewrites() {
  if (batchTimer !== null) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  pending.clear();
  stopViewport();
  for (const req of inflight) req.controller.abort();
  inflight.clear();
  cache.clear();
  transientFailures.clear();
  attempts.clear();
  pausedUntil = 0;
  contextAtWork = null;
  stopTiming();
  routeMarkedAt = -Infinity;
}

// Work belongs to one tenant + user + generation. Anything queued, in flight
// or cached for another is dropped before new work starts.
function claimContext() {
  const id = contextId();
  if (contextAtWork !== null && contextAtWork !== id) resetDemoRewrites();
  contextAtWork = id;
}

function onContextChange(next, prev) {
  if (store.getters['demoMode/status'] === DEMO_STATUS.OFF) {
    resetDemoRewrites();
    return;
  }
  if (contextAtWork !== null && contextAtWork !== contextId()) resetDemoRewrites();
  if (next?.[0] === DEMO_STATUS.READY && prev?.[0] !== DEMO_STATUS.READY) startTiming();
  schedule();
}

function ensureWatching() {
  if (watchScope) return;
  watchScope = effectScope(true);
  watchScope.run(() => {
    store.watch(
      (state, getters) => [
        getters['demoMode/status'],
        getters['demoMode/generation'],
        getters['tenant/current_tenant_slug'] ?? null,
        getters['auth/current_user']?.id ?? null,
        getters['auth/isAuthenticated'],
      ],
      onContextChange,
      { flush: 'sync' }
    );
  });
}

function readyAt(item, quietAt) {
  return Math.max(item.notBefore || 0, Math.min(quietAt, item.queuedAt + REWRITE_LIMITS.nameMapSettleMaxMs));
}

function nameMapQuietAt() {
  return demoNameMapChangedAt() + REWRITE_LIMITS.nameMapSettleMs;
}

// Earliest time anything pending may be sent: the batch delay, a 429 pause,
// and each retry's own cooldown.
function nextWake(now) {
  const quietAt = nameMapQuietAt();
  let earliest = Infinity;
  for (const item of pending.values()) earliest = Math.min(earliest, readyAt(item, quietAt));
  if (earliest === Infinity) return null;
  return Math.max(earliest, pausedUntil, now);
}

function schedule(delay = REWRITE_LIMITS.batchDelayMs) {
  if (batchTimer !== null || !pending.size) return;
  batchTimer = setTimeout(() => {
    batchTimer = null;
    pump();
  }, delay);
}

function bodyBytes(body) {
  return new TextEncoder().encode(JSON.stringify(body)).length;
}

function buildBody(items) {
  const fakes = {};
  for (const item of items) Object.assign(fakes, item.fakes);
  return {
    blocks: items.map((item) => ({
      id: item.id,
      text: item.text,
      record: item.record == null ? null : { type: 'patient', id: item.record },
      date_offset_days: null,
      fakes: item.fakes,
    })),
    fakes,
  };
}

// The next batch from the queue, inside every server limit. A block that
// cannot fit even alone is resolved as redact rather than sent.
function takeBatch() {
  const batch = [];
  let chars = 0;
  const fakes = {};
  const now = Date.now();
  const quietAt = nameMapQuietAt();
  const queue = [...pending.values()];
  const ordered = onScreen.size ? [...queue.filter(isOnScreen), ...queue.filter((i) => !isOnScreen(i))] : queue;
  for (const item of ordered) {
    if (batch.length >= REWRITE_LIMITS.blocksPerRequest) break;
    if (readyAt(item, quietAt) > now) continue;
    const merged = { ...fakes, ...item.fakes };
    const tooBig =
      chars + item.chars > REWRITE_LIMITS.requestChars ||
      !fakesFit(merged) ||
      bodyBytes(buildBody([...batch, item])) > REWRITE_LIMITS.bodyBytes;
    if (tooBig) {
      if (batch.length) continue;
      pending.delete(item.key);
      unwatchItem(item);
      resolveItems([item], () => ({ status: 'redact', reason: 'oversized' }));
      continue;
    }
    batch.push(item);
    chars += item.chars;
    Object.assign(fakes, item.fakes);
  }
  for (const item of batch) {
    pending.delete(item.key);
    unwatchItem(item);
  }
  return batch;
}

function resolveItems(items, resultFor) {
  for (const item of items) {
    const result = resultFor(item);
    if (result.status === 'redact' && TRANSIENT.has(result.reason)) {
      transientFailures.set(item.key, { reason: result.reason, retryAt: result.retryAt || 0 });
    } else {
      transientFailures.delete(item.key);
      cacheSet(item.key, result);
    }
    deliver(item.subscribers, item.key, { status: result.status, text: result.text, reason: result.reason }, item.fakes);
  }
}

function pump() {
  if (!sendingAllowed()) return;
  claimContext();
  const now = Date.now();
  if (now < pausedUntil) {
    schedule(pausedUntil - now);
    return;
  }
  while (inflight.size < REWRITE_LIMITS.inFlight && pending.size) {
    const batch = takeBatch();
    if (!batch.length) break;
    send(batch);
  }
  // Retries still cooling down wait for their own time.
  const wake = nextWake(Date.now());
  if (wake !== null && inflight.size < REWRITE_LIMITS.inFlight) schedule(Math.max(0, wake - Date.now()));
}

// Retry-After is seconds or an HTTP date. Clamped; the default when absent.
export function retryAfterMs(value, now = Date.now()) {
  const { retryAfterMinMs: min, retryAfterMaxMs: max, retryAfterDefaultMs: fallback } = REWRITE_LIMITS;
  let ms = NaN;
  if (value != null && String(value).trim() !== '') {
    const text = String(value).trim();
    ms = /^\d+(\.\d+)?$/.test(text) ? Number(text) * 1000 : Date.parse(text) - now;
  }
  if (!Number.isFinite(ms)) ms = fallback;
  return Math.min(max, Math.max(min, ms));
}

// Transport failures: a 429 and no response at all (timeout, network) are
// transient; any other HTTP status is terminal.
export function classifyFailure(error, now = Date.now()) {
  const status = error?.response?.status;
  if (status === 429) {
    const header = error.response.headers?.['retry-after'] ?? error.response.headers?.get?.('retry-after');
    return { status: 'redact', reason: 'rate-limit', retryAt: now + retryAfterMs(header, now) };
  }
  if (status) return { status: 'redact', reason: 'http' };
  const code = error?.code;
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(error?.message || '')) {
    return { status: 'redact', reason: 'timeout' };
  }
  return { status: 'redact', reason: 'network' };
}

// Validates the reply envelope. Missing results redact their blocks; a
// duplicate or foreign id, or anything malformed, redacts the whole request.
export function parseRewriteReply(data, ids) {
  const redactAll = new Map(ids.map((id) => [id, { status: 'redact', reason: 'malformed' }]));
  if (!data || typeof data !== 'object' || !Array.isArray(data.results)) return redactAll;
  const wanted = new Set(ids);
  const out = new Map();
  for (const r of data.results) {
    if (!r || typeof r !== 'object' || typeof r.id !== 'string') return redactAll;
    if (!wanted.has(r.id) || out.has(r.id)) return redactAll;
    if (r.status === 'ok' && typeof r.text === 'string' && r.text.trim()) {
      out.set(r.id, { status: 'ok', text: r.text });
    } else {
      out.set(r.id, { status: 'redact', reason: 'backend' });
    }
  }
  for (const id of ids) if (!out.has(id)) out.set(id, { status: 'redact', reason: 'backend' });
  return out;
}

function abortIfUnneeded(req) {
  if (!inflight.has(req) || req.items.some((item) => item.subscribers.size)) return;
  inflight.delete(req);
  req.controller.abort();
  stats.aborted += 1;
  for (const item of req.items) {
    const sent = (attempts.get(item.key) || 1) - 1;
    if (sent > 0) attempts.set(item.key, sent);
    else attempts.delete(item.key);
  }
  pump();
}

function send(items) {
  const controller = new AbortController();
  const req = { controller, items };
  inflight.add(req);
  req.sentAt = clock();
  for (const item of items) item.req = req;
  const sentContext = contextId();
  stats.requests += 1;
  const body = buildBody(items);
  for (const item of items) attempts.set(item.key, (attempts.get(item.key) || 0) + 1);
  const finish = (replyFor) => {
    if (!inflight.has(req)) return;
    inflight.delete(req);
    if (controller.signal.aborted || contextId() !== sentContext || !sendingAllowed()) return;
    resolveItems(items, replyFor);
    pump();
  };
  axios
    .post(REWRITE_URL, body, { signal: controller.signal, timeout: REWRITE_LIMITS.timeoutMs })
    .then(
      (response) => {
        const parsed = parseRewriteReply(response?.data, items.map((i) => i.id));
        finish((item) => parsed.get(item.id));
      },
      (error) => {
        if (!axios.isCancel?.(error)) stats.failed += 1;
        const failure = classifyFailure(error);
        if (failure.retryAt) pausedUntil = Math.max(pausedUntil, failure.retryAt);
        finish(() => failure);
      }
    );
}

let nextId = 0;

// Asks for a rewrite of one block. `cb` is called at most once, with
// { key, status: 'ok', text, fakes } or { key, status: 'redact', reason,
// fakes }. Returns a cancel function. Nothing is sent for empty text or while
// Demo Mode is off; while it is activating the block waits for ready.
// `allowRetry` is set only by a mount or KeepAlive reactivation: a block whose
// last outcome was transient is then sent once more (within the per-block
// attempt budget, after any cooldown); otherwise it stays blurred.
export function requestDemoRewrite({ text, record = null }, cb, { allowRetry = false, el = null } = {}) {
  ensureWatching();
  if (store.getters['demoMode/status'] === DEMO_STATUS.OFF) return () => {};
  claimContext();
  if (timing.startedAt === null) startTiming();
  const desc = describeRewrite({ text, record });
  if (!desc) return () => {};
  let active = true;
  const once = (result) => {
    if (!active) return;
    active = false;
    cb(result);
  };
  if (!desc.sendable) {
    queueMicrotask(() => once({ key: desc.key, status: 'redact', reason: 'oversized', fakes: desc.fakes }));
    return () => {
      active = false;
    };
  }
  const cached = cacheGet(desc.key);
  if (cached) {
    queueMicrotask(() => once({ key: desc.key, ...cached, fakes: desc.fakes }));
    return () => {
      active = false;
    };
  }
  let item = pending.get(desc.key);
  if (!item) {
    for (const req of inflight) item = item || req.items.find((i) => i.key === desc.key);
  }
  const transient = transientFailures.get(desc.key);
  let notBefore = 0;
  if (!item && transient) {
    if (!allowRetry || (attempts.get(desc.key) || 0) >= REWRITE_LIMITS.attemptsPerBlock) {
      queueMicrotask(() => once({ key: desc.key, status: 'redact', reason: transient.reason, fakes: desc.fakes }));
      return () => {
        active = false;
      };
    }
    notBefore = transient.retryAt;
  }
  if (!item) {
    nextId += 1;
    item = {
      id: `b${nextId}`,
      key: desc.key,
      text: desc.text,
      record: desc.record,
      fakes: desc.fakes,
      chars: codePoints(desc.text),
      notBefore,
      queuedAt: Date.now(),
      subscribers: new Set(),
      watchers: new Map(),
    };
    pending.set(desc.key, item);
    schedule();
  }
  item.subscribers.add(once);
  if (pending.get(item.key) === item) watchSubscriber(item, once, el);
  return ({ superseded = false } = {}) => {
    const waiting = active;
    active = false;
    item.subscribers.delete(once);
    unwatchSubscriber(item, once);
    if (!item.subscribers.size && pending.get(item.key) === item) pending.delete(item.key);
    const req = item.req;
    if (!waiting || !req || !inflight.has(req)) return;
    if (superseded) stats.superseded += 1;
    if (superseded || req.sentAt < routeMarkedAt) queueMicrotask(() => abortIfUnneeded(req));
  };
}

export function resetDemoRewriteForTests() {
  resetDemoRewrites();
  watchScope?.stop();
  watchScope = null;
  stats.requests = 0;
  stats.rejected = 0;
  stats.failed = 0;
  stats.superseded = 0;
  stats.aborted = 0;
  timing.base = { ...stats };
  nextId = 0;
}

// --- Count-only census for the Demo Mode walkthrough ---
//
// How much visible prose is readable, and why the rest is blurred. Counts
// only: never text, ids or keys. Mounted DemoText instances report a state;
// any other visible `.sensitive[data-demo-blur]` element is an unconverted
// site. In a browser console: window.__demoRewriteCensus().
const censusEntries = new Map(); // element -> () => state

export function trackDemoTextCensus(el, stateFn) {
  if (el) censusEntries.set(el, stateFn);
}

export function untrackDemoTextCensus(el) {
  censusEntries.delete(el);
}

function isVisible(el) {
  return el.isConnected && el.getClientRects().length > 0;
}

export function demoRewriteCensus() {
  const counts = {};
  const bump = (state) => {
    counts[state] = (counts[state] || 0) + 1;
  };
  let waiting = false;
  for (const [el, stateFn] of censusEntries) {
    if (!el.isConnected) {
      censusEntries.delete(el);
      continue;
    }
    const state = stateFn();
    if (state === 'pending') waiting = true;
    if (isVisible(el)) bump(state);
  }
  if (typeof document !== 'undefined') {
    document.querySelectorAll('.sensitive[data-demo-blur]').forEach((el) => {
      if (!censusEntries.has(el) && isVisible(el)) bump('unconverted');
    });
  }
  const since = (t) => (t === null || timing.startedAt === null ? null : Math.round(t - timing.startedAt));
  counts.timing = {
    firstReadableMs: since(timing.firstReadableAt),
    settledMs: waiting ? null : since(timing.lastReadableAt),
    requests: stats.requests - timing.base.requests,
    superseded: stats.superseded - timing.base.superseded,
    aborted: stats.aborted - timing.base.aborted,
  };
  return counts;
}

if (typeof window !== 'undefined') window.__demoRewriteCensus = demoRewriteCensus;
